import { useMemo, useState, type KeyboardEvent, type MouseEvent } from "react";
import { useNavigate } from "react-router";
import { IoGitNetworkOutline, IoChevronDown, IoChevronForward } from "react-icons/io5";
import { SeverityBadge } from "../SeverityBadge/SeverityBadge";
import { formatNZD } from "../../utils/formatters";
import { groupPackets } from "../../utils/groupPackets";
import type { PacketResult } from "../../types/shadow-run";
import type { PayGroupSection, PayPeriodGroup } from "../../utils/groupPackets";
import styles from "./PacketTable.module.css";

export interface PacketTableProps {
  packets: PacketResult[];
  isLoading: boolean;
  isError: boolean;
  batchId: string;
  orgCategory: string;
  orgId: string;
}

const TOTAL_COLS = 13;

function VarianceCell({ fsc, pacific }: { fsc: number; pacific: number }) {
  const diff = pacific - fsc;
  const isZero = Math.abs(diff) < 0.005;

  if (isZero) {
    return <span className={styles.varianceZero}>—</span>;
  }

  const colorClass =
    diff > 0 ? styles.variancePositive : styles.varianceNegative;

  return <span className={colorClass}>{formatNZD(diff)}</span>;
}

function SkeletonRows() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <tr key={i} className={styles.row} data-testid="skeleton-row">
          {Array.from({ length: TOTAL_COLS }).map((_, j) => (
            <td key={j} className={styles.td}>
              <div className={styles.skeleton} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function PacketRow({
  row,
  onRowClick,
  onGraphClick,
}: {
  row: PacketResult;
  onRowClick: (packetId: string) => void;
  onGraphClick: (e: MouseEvent<HTMLButtonElement>, packetId: string) => void;
}) {
  return (
    <tr
      key={row.packet_id}
      className={styles.row}
      onClick={() => onRowClick(row.packet_id)}
      onKeyDown={(e: KeyboardEvent<HTMLTableRowElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick(row.packet_id);
        }
      }}
      tabIndex={0}
      role="button"
      data-testid="packet-row"
    >
      <td className={`${styles.td} ${styles.stickyId}`}>
        <span className={styles.packetId}>{row.packet_id}</span>
      </td>
      <td className={`${styles.td} ${styles.stickyStatus} ${styles.stickyShadow} ${styles.centerCol}`}>
        <SeverityBadge tier={row.severity_tier} />
      </td>
      <td className={`${styles.td} ${styles.numCol}`}>
        {formatNZD(row.fsc_gross)}
      </td>
      <td className={`${styles.td} ${styles.numCol}`}>
        {formatNZD(row.fsc_paye)}
      </td>
      <td className={`${styles.td} ${styles.numCol} ${styles.groupEnd}`}>
        {formatNZD(row.fsc_net)}
      </td>
      <td className={`${styles.td} ${styles.numCol}`}>
        {formatNZD(row.pacific_gross)}
      </td>
      <td className={`${styles.td} ${styles.numCol}`}>
        {formatNZD(row.pacific_paye)}
      </td>
      <td className={`${styles.td} ${styles.numCol} ${styles.groupEnd}`}>
        {formatNZD(row.pacific_net)}
      </td>
      <td className={`${styles.td} ${styles.numCol}`}>
        <VarianceCell fsc={row.fsc_gross} pacific={row.pacific_gross} />
      </td>
      <td className={`${styles.td} ${styles.numCol}`}>
        <VarianceCell fsc={row.fsc_paye} pacific={row.pacific_paye} />
      </td>
      <td className={`${styles.td} ${styles.numCol} ${styles.groupEnd}`}>
        <VarianceCell fsc={row.fsc_net} pacific={row.pacific_net} />
      </td>
      <td className={`${styles.td} ${styles.numCol}`}>
        {row.variance_field_count}
      </td>
      <td className={styles.td}>
        <button
          type="button"
          className={styles.graphButton}
          onClick={(e) => onGraphClick(e, row.packet_id)}
          onKeyDown={(e) => e.stopPropagation()}
          aria-label={`View graph for ${row.packet_id}`}
          data-testid="graph-button"
        >
          <IoGitNetworkOutline size={16} />
        </button>
      </td>
    </tr>
  );
}

function countPacketsInSection(section: PayGroupSection): number {
  return section.periods.reduce((sum, p) => sum + p.packets.length, 0);
}

function GroupedRows({
  sections,
  onRowClick,
  onGraphClick,
}: {
  sections: PayGroupSection[];
  onRowClick: (packetId: string) => void;
  onGraphClick: (e: MouseEvent<HTMLButtonElement>, packetId: string) => void;
}) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );
  const [collapsedPeriods, setCollapsedPeriods] = useState<Set<string>>(
    new Set(),
  );

  function toggleGroup(code: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }

  function togglePeriod(key: string) {
    setCollapsedPeriods((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  return (
    <>
      {sections.map((section) => {
        const groupCollapsed = collapsedGroups.has(section.code);
        const totalPackets = countPacketsInSection(section);

        return (
          <GroupSection
            key={section.code}
            section={section}
            isCollapsed={groupCollapsed}
            onToggle={() => toggleGroup(section.code)}
            totalPackets={totalPackets}
            collapsedPeriods={collapsedPeriods}
            onTogglePeriod={togglePeriod}
            onRowClick={onRowClick}
            onGraphClick={onGraphClick}
          />
        );
      })}
    </>
  );
}

function GroupSection({
  section,
  isCollapsed,
  onToggle,
  totalPackets,
  collapsedPeriods,
  onTogglePeriod,
  onRowClick,
  onGraphClick,
}: {
  section: PayGroupSection;
  isCollapsed: boolean;
  onToggle: () => void;
  totalPackets: number;
  collapsedPeriods: Set<string>;
  onTogglePeriod: (key: string) => void;
  onRowClick: (packetId: string) => void;
  onGraphClick: (e: MouseEvent<HTMLButtonElement>, packetId: string) => void;
}) {
  const ChevronIcon = isCollapsed ? IoChevronForward : IoChevronDown;

  return (
    <>
      <tr
        className={styles.payGroupHeaderRow}
        onClick={onToggle}
        data-testid="pay-group-header"
      >
        <td colSpan={TOTAL_COLS} className={styles.payGroupHeaderCell}>
          <div className={styles.payGroupHeaderContent}>
            <ChevronIcon size={14} className={styles.chevronIcon} />
            <span className={styles.payGroupName}>
              {section.name}
              {section.code !== section.name && ` (${section.code})`}
            </span>
            <span className={styles.payGroupCount}>
              {totalPackets} {totalPackets === 1 ? "packet" : "packets"}
            </span>
          </div>
        </td>
      </tr>
      {!isCollapsed &&
        section.periods.map((period) => {
          const periodKey = `${section.code}|${period.periodStart}|${period.periodEnd}`;
          const periodCollapsed = collapsedPeriods.has(periodKey);

          return (
            <PeriodSection
              key={periodKey}
              period={period}
              isCollapsed={periodCollapsed}
              onToggle={() => onTogglePeriod(periodKey)}
              onRowClick={onRowClick}
              onGraphClick={onGraphClick}
            />
          );
        })}
    </>
  );
}

function PeriodSection({
  period,
  isCollapsed,
  onToggle,
  onRowClick,
  onGraphClick,
}: {
  period: PayPeriodGroup;
  isCollapsed: boolean;
  onToggle: () => void;
  onRowClick: (packetId: string) => void;
  onGraphClick: (e: MouseEvent<HTMLButtonElement>, packetId: string) => void;
}) {
  const ChevronIcon = isCollapsed ? IoChevronForward : IoChevronDown;

  return (
    <>
      <tr
        className={styles.payPeriodHeaderRow}
        onClick={onToggle}
        data-testid="pay-period-header"
      >
        <td colSpan={TOTAL_COLS} className={styles.payPeriodHeaderCell}>
          <div className={styles.payPeriodHeaderContent}>
            <ChevronIcon size={12} className={styles.chevronIcon} />
            <span className={styles.payPeriodLabel}>
              {period.periodLabel}
            </span>
            <span className={styles.payPeriodCount}>
              {period.packets.length}{" "}
              {period.packets.length === 1 ? "packet" : "packets"}
            </span>
          </div>
        </td>
      </tr>
      {!isCollapsed &&
        period.packets.map((row) => (
          <PacketRow
            key={row.packet_id}
            row={row}
            onRowClick={onRowClick}
            onGraphClick={onGraphClick}
          />
        ))}
    </>
  );
}

function TableHead() {
  return (
    <thead>
      <tr className={styles.groupRow}>
        <th className={`${styles.groupTh} ${styles.stickyId}`} />
        <th className={`${styles.groupTh} ${styles.stickyStatus} ${styles.stickyShadow} ${styles.centerCol}`} />
        <th className={styles.groupTh} colSpan={3}>
          FSC
        </th>
        <th className={styles.groupTh} colSpan={3}>
          Pacific
        </th>
        <th className={styles.groupTh} colSpan={3}>
          Variance
        </th>
        <th className={styles.groupTh} />
        <th className={styles.groupTh} />
      </tr>
      <tr className={styles.headerRow}>
        <th className={`${styles.th} ${styles.stickyId}`}>Packet ID</th>
        <th className={`${styles.th} ${styles.stickyStatus} ${styles.stickyShadow} ${styles.centerCol}`}>Status</th>
        <th className={`${styles.th} ${styles.numCol}`}>Gross</th>
        <th className={`${styles.th} ${styles.numCol}`}>PAYE</th>
        <th className={`${styles.th} ${styles.numCol} ${styles.groupEnd}`}>Net</th>
        <th className={`${styles.th} ${styles.numCol}`}>Gross</th>
        <th className={`${styles.th} ${styles.numCol}`}>PAYE</th>
        <th className={`${styles.th} ${styles.numCol} ${styles.groupEnd}`}>Net</th>
        <th className={`${styles.th} ${styles.numCol}`}>Gross&thinsp;&Delta;</th>
        <th className={`${styles.th} ${styles.numCol}`}>PAYE&thinsp;&Delta;</th>
        <th className={`${styles.th} ${styles.numCol} ${styles.groupEnd}`}>Net&thinsp;&Delta;</th>
        <th className={`${styles.th} ${styles.numCol}`}>Count</th>
        <th className={styles.th} />
      </tr>
    </thead>
  );
}

export function PacketTable({
  packets,
  isLoading,
  isError,
  batchId,
  orgCategory,
  orgId,
}: PacketTableProps) {
  const navigate = useNavigate();
  const sections = useMemo(() => groupPackets(packets), [packets]);
  const isGrouped = sections.length > 0;

  function handleRowClick(packetId: string) {
    navigate(
      `/shadow-runs/${batchId}/packets/${packetId}?org_category=${encodeURIComponent(orgCategory)}&org_id=${encodeURIComponent(orgId)}`,
    );
  }

  function handleGraphClick(e: MouseEvent<HTMLButtonElement>, packetId: string) {
    e.stopPropagation();
    navigate(
      `/shadow-runs/${batchId}/packets/${packetId}/graph?org_category=${encodeURIComponent(orgCategory)}&org_id=${encodeURIComponent(orgId)}`,
    );
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <TableHead />
        <tbody>
          {isLoading ? (
            <SkeletonRows />
          ) : isError ? (
            <tr>
              <td colSpan={TOTAL_COLS} className={styles.td}>
                <div className={styles.emptyRow} data-testid="table-error">
                  Unable to load packets. Please try again.
                </div>
              </td>
            </tr>
          ) : packets.length === 0 ? (
            <tr>
              <td colSpan={TOTAL_COLS} className={styles.td}>
                <div className={styles.emptyRow} data-testid="table-empty">
                  No packets found.
                </div>
              </td>
            </tr>
          ) : isGrouped ? (
            <GroupedRows
              sections={sections}
              onRowClick={handleRowClick}
              onGraphClick={handleGraphClick}
            />
          ) : (
            packets.map((row) => (
              <PacketRow
                key={row.packet_id}
                row={row}
                onRowClick={handleRowClick}
                onGraphClick={handleGraphClick}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
